import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    ArrowLeft, RefreshCw, Server, Database, CheckCircle,
    XCircle, AlertTriangle, Clock, Play, Zap, Shield,
    HardDrive, Cpu, Wifi, Settings,
} from 'lucide-react';

const CRON_TASKS = [
    { key: 'run-all', label: 'Run All Tasks', desc: 'Execute all scheduled maintenance tasks at once', icon: Zap, color: 'text-[#E04343]' },
    { key: 'expire-rate-limits', label: 'Expire Rate Limits', desc: 'Clean up expired rate limit entries from cache', icon: Clock, color: 'text-blue-600' },
    { key: 'retry-failed-webhooks', label: 'Retry Failed Webhooks', desc: 'Retry webhook deliveries that previously failed', icon: RefreshCw, color: 'text-orange-600' },
    { key: 'sync-nipost-statuses', label: 'Sync NIPOST Statuses', desc: 'Pull latest shipment statuses from NIPOST API', icon: Wifi, color: 'text-green-600' },
    { key: 'disable-failing-webhooks', label: 'Disable Failing Webhooks', desc: 'Auto-disable webhooks with high failure rates', icon: XCircle, color: 'text-red-600' },
    { key: 'check-low-wallet-balances', label: 'Check Low Wallets', desc: 'Send alerts for partners with low wallet balances', icon: AlertTriangle, color: 'text-yellow-600' },
    { key: 'cleanup-logs', label: 'Cleanup Logs', desc: 'Remove old API request logs (30+ days)', icon: HardDrive, color: 'text-gray-600' },
    { key: 'expire-api-keys', label: 'Expire API Keys', desc: 'Deactivate API keys past their expiration date', icon: Shield, color: 'text-purple-600' },
    { key: 'cleanup-webhook-deliveries', label: 'Cleanup Deliveries', desc: 'Remove old webhook delivery records', icon: Database, color: 'text-cyan-600' },
];

function SystemHealth() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [health, setHealth] = React.useState<any>(null);
    const [stats, setStats] = React.useState<any>(null);
    const [runningTask, setRunningTask] = React.useState<string>('');
    const [cronResults, setCronResults] = React.useState<Record<string, any>>({});

    React.useEffect(() => { loadSystem(); }, []);

    const loadSystem = () => {
        setLoading(true);
        Promise.all([
            ApiGatewayApis.getSystemHealth(),
            ApiGatewayApis.getSystemStats(),
        ]).then(([healthRes, statsRes]: any[]) => {
            setLoading(false);
            if (healthRes?.data?.data) setHealth(healthRes.data.data);
            if (statsRes?.data?.data) setStats(statsRes.data.data);
        }).catch((error: any) => {
            setLoading(false);
            toast.error('Failed to load system data');
        });
    };

    const runCron = (taskKey: string) => {
        setRunningTask(taskKey);
        const promise = taskKey === 'run-all'
            ? ApiGatewayApis.runAllCronTasks()
            : ApiGatewayApis.runCronTask(taskKey);

        promise.then((response: any) => {
            setRunningTask('');
            if (response?.data?.success) {
                toast.success(`Task "${taskKey}" completed successfully`);
                setCronResults(prev => ({ ...prev, [taskKey]: { success: true, data: response.data.data, time: new Date() } }));
            } else {
                toast.warn(response?.data?.message || `Task "${taskKey}" returned an unexpected result`);
                setCronResults(prev => ({ ...prev, [taskKey]: { success: false, message: response?.data?.message, time: new Date() } }));
            }
        }).catch((error: any) => {
            setRunningTask('');
            toast.error(error?.response?.data?.message || `Task "${taskKey}" failed`);
            setCronResults(prev => ({ ...prev, [taskKey]: { success: false, message: error?.response?.data?.message || 'Failed', time: new Date() } }));
        });
    };

    const getHealthIcon = (status: string) => {
        switch (status) {
            case 'ok': return <CheckCircle size={18} className='text-green-600' />;
            case 'degraded': return <AlertTriangle size={18} className='text-yellow-600' />;
            case 'down': return <XCircle size={18} className='text-red-600' />;
            default: return <Clock size={18} className='text-gray-400' />;
        }
    };

    const getHealthBadge = (status: string) => {
        switch (status) {
            case 'ok': return 'bg-green-100 text-green-800';
            case 'degraded': return 'bg-yellow-100 text-yellow-800';
            case 'down': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    return (
        <>
            <AdminSidebar title="System" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="System Health & Cron" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>

                    {/* Header */}
                    <div className='flex justify-between items-center mb-5'>
                        <div className='flex items-center gap-3'>
                            <button onClick={() => navigate('/admin/api-gateway')} className='text-gray-500 hover:text-[#E04343]'>
                                <ArrowLeft size={20} />
                            </button>
                            <h1 className='text-[20px] font-bold text-[#263238]'>System Health & Cron Tasks</h1>
                        </div>
                        <button onClick={loadSystem}
                            className='bg-white border border-[#DADADA] p-2 rounded-[5px] hover:bg-gray-50' title='Refresh'>
                            <RefreshCw size={18} className={loading ? 'animate-spin text-[#E04343]' : 'text-gray-600'} />
                        </button>
                    </div>

                    {loading ? (
                        <div className='flex justify-center py-20'>
                            <Oval visible={true} height="40" width="40" color="#E04343" secondaryColor="#FCBF94" />
                        </div>
                    ) : (
                        <>
                            {/* System Health Panel */}
                            <div className='bg-white shadow-sm rounded-[10px] p-5 mb-6'>
                                <div className='flex justify-between items-center mb-4'>
                                    <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                        <Server size={18} /> System Health
                                    </h3>
                                    {health?.status && (
                                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${getHealthBadge(health.status)}`}>
                                            {health.status.toUpperCase()}
                                        </span>
                                    )}
                                </div>

                                {health?.components ? (
                                    <div className='grid md:grid-cols-3 lg:grid-cols-4 gap-3'>
                                        {Object.entries(health.components).map(([key, comp]: [string, any]) => (
                                            <div key={key} className='flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3'>
                                                {getHealthIcon(comp?.status || 'unknown')}
                                                <div>
                                                    <span className='text-[13px] font-medium capitalize block'>{key.replace(/_/g, ' ')}</span>
                                                    <span className={`text-[11px] font-semibold ${comp?.status === 'ok' ? 'text-green-600' :
                                                        comp?.status === 'degraded' ? 'text-yellow-600' : 'text-red-600'
                                                        }`}>
                                                        {comp?.status || 'N/A'}
                                                        {comp?.latency && ` · ${comp.latency}ms`}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className='text-[13px] text-[#808080]'>Unable to fetch health status</p>
                                )}

                                {health?.timestamp && (
                                    <p className='text-[11px] text-[#808080] mt-3'>
                                        Last checked: {new Date(health.timestamp).toLocaleString()}
                                    </p>
                                )}
                            </div>

                            {/* System Stats */}
                            {stats && (
                                <div className='grid lg:grid-cols-4 md:grid-cols-2 gap-4 mb-6'>
                                    {[
                                        { label: 'Total Partners', value: stats.partners?.total || 0, sub: `${stats.partners?.active || 0} active`, color: 'border-blue-500' },
                                        { label: 'Total API Keys', value: stats.api_keys?.total || 0, sub: `${stats.api_keys?.active || 0} active`, color: 'border-green-500' },
                                        { label: 'Total Shipments', value: stats.shipments?.total || 0, sub: `${stats.shipments?.from_api || 0} via API`, color: 'border-purple-500' },
                                        { label: 'Webhook Endpoints', value: stats.webhooks?.total || 0, sub: `${stats.webhooks?.active || 0} active`, color: 'border-orange-500' },
                                    ].map((s) => (
                                        <div key={s.label} className={`bg-white shadow-sm rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[2px] ${s.color}`}>
                                            <h5 className='text-[12px] text-[#808080]'>{s.label}</h5>
                                            <h1 className='text-[24px] font-semibold mt-1'>{s.value.toLocaleString()}</h1>
                                            <p className='text-[11px] text-[#808080] mt-1'>{s.sub}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Cron Tasks */}
                            <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                <div className='flex justify-between items-center mb-4'>
                                    <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                        <Settings size={18} /> Cron Tasks
                                    </h3>
                                    <p className='text-[12px] text-[#808080]'>Manually trigger maintenance tasks</p>
                                </div>

                                <div className='space-y-2'>
                                    {CRON_TASKS.map((task) => {
                                        const IconComp = task.icon;
                                        const result = cronResults[task.key];
                                        const isRunning = runningTask === task.key;

                                        return (
                                            <div key={task.key}
                                                className={`flex items-center justify-between rounded-lg px-4 py-3 transition ${task.key === 'run-all' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                                                    }`}>
                                                <div className='flex items-center gap-3'>
                                                    <IconComp size={18} className={task.color} />
                                                    <div>
                                                        <span className='text-[13px] font-medium text-[#263238]'>{task.label}</span>
                                                        <p className='text-[11px] text-[#808080]'>{task.desc}</p>
                                                    </div>
                                                </div>

                                                <div className='flex items-center gap-3'>
                                                    {/* Last result badge */}
                                                    {result && (
                                                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${result.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                            }`}>
                                                            {result.success ? 'Done' : 'Failed'} · {result.time ? new Date(result.time).toLocaleTimeString() : ''}
                                                        </span>
                                                    )}

                                                    <button
                                                        onClick={() => runCron(task.key)}
                                                        disabled={!!runningTask}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-[12px] font-medium disabled:opacity-40 transition ${task.key === 'run-all'
                                                            ? 'bg-[#E04343] text-white hover:bg-[#c73838]'
                                                            : 'bg-white border border-[#DADADA] text-gray-700 hover:bg-gray-100'
                                                            }`}
                                                    >
                                                        {isRunning ? (
                                                            <Oval visible={true} height="14" width="14" color={task.key === 'run-all' ? '#fff' : '#E04343'} secondaryColor="#FCBF94" />
                                                        ) : (
                                                            <Play size={13} />
                                                        )}
                                                        {isRunning ? 'Running...' : 'Run'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default SystemHealth;